<template>
  <div class="home-layout">
    <div class="home-content">
      <div class="project-list-container">
        <div class="project-list-header">
          <div class="header-title">
            <h3>项目列表</h3>
            <span class="project-count">({{ filteredProjectList.length }}/{{ projectList.length }})</span>
            <span v-if="projectSortBy" class="sort-indicator">
              <el-tag size="small" type="primary">
                {{ getSortLabel(projectSortBy) }}
              </el-tag>
            </span>
          </div>
          <div class="header-actions">
            <el-select
              v-model="projectSortBy"
              clearable
              class="sort-select"
              placeholder="排序"
            >
              <el-option label="创建时间(新→旧)" value="createTime" />
              <el-option label="创建时间(旧→新)" value="createTimeAsc" />
              <el-option label="名称(A→Z)" value="name" />
              <el-option label="名称(Z→A)" value="nameDesc" />
            </el-select>
            <el-button
              v-if="projectSortBy"
              size="small"
              text
              type="primary"
              @click="clearSort"
            >
              清除排序
            </el-button>
            <el-input
              v-model="projectSearchText"
              placeholder="搜索项目"
              class="project-search"
              clearable
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
          </div>
        </div>
        <div class="project-list">
          <div
            v-for="project in filteredProjectList"
            :key="project.id"
            class="project-card"
            @mouseenter="handleProjectHover(project.id)"
            @mouseleave="handleProjectLeave(project.id)"
            @click="handleProjectClick(project)"
          >
            <div class="project-thumbnail">
              <el-image
                v-if="project.imgPath"
                :src="project.imgPath"
                fit="cover"
                class="thumbnail-image"
                :preview-src-list="[project.imgPath]"
                :initial-index="0"
                preview-teleported
              >
                <template #error>
                  <div class="thumbnail-placeholder">
                    <el-icon><User /></el-icon>
                  </div>
                </template>
              </el-image>
              <div v-else class="thumbnail-placeholder">
                <el-icon><User /></el-icon>
              </div>
            </div>
            <div class="project-info">
              <div class="project-name" :title="project.projectName">{{ project.projectName }}</div>
              <div class="project-meta project-id" :title="`ID: ${project.id}`">
                <el-icon><Tickets /></el-icon>
                <span>{{ project.id }}</span>
              </div>
              <div class="project-date">
                <el-icon><Calendar /></el-icon>
                <span>{{ formatDate(project.createDate) }}</span>
              </div>
              <div class="project-copy-preview" :title="project.description || '暂无文案'">
                {{ project.description || '暂无文案' }}
              </div>
              <div v-show="hoveredProjectId === project.id" class="project-overlay">
                <el-button
                  type="primary"
                  size="small"
                  text
                  icon="Edit"
                  @click.stop="handleRenameProject(project)"
                >
                  重命名
                </el-button>
                <el-button
                  type="primary"
                  size="small"
                  text
                  icon="CopyDocument"
                  @click.stop="handleCopyProject(project)"
                >
                  复制
                </el-button>
                <el-button
                  type="danger"
                  size="small"
                  text
                  icon="Delete"
                  @click.stop="handleDeleteProject(project)"
                >
                  删除
                </el-button>
              </div>
            </div>
          </div>
          <div class="empty-state" @click="handleNewProject">
            <el-icon :size="48" color="var(--accent-blue)"><Plus /></el-icon>
            <p>创建项目</p>
          </div>
        </div>
      </div>
    </div>

    <el-dialog v-model="renameDialogVisible" title="重命名项目" width="300px" align-center>
      <el-form>
        <el-form-item label="项目名称">
          <el-input v-model="newProjectName" placeholder="请输入新的项目名称" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="renameDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmRename">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="newProjectDialogVisible" title="新建项目" width="300px" align-center>
      <el-form>
        <el-form-item label="项目名称">
          <el-input v-model="newProjectNameInput" placeholder="请输入项目名称" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="newProjectDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmNewProject">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { Calendar, Plus, Search, Tickets, User } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRouter } from 'vue-router'
import {
  getProjectList,
  addProject,
  deleteProject,
  renameProject,
  copyProject,
  addOrUpdateProjectParams,
  getProjectsItemInfo,
} from '@/api/portal/projects'

const TOOL_ID = 64
const TOOL_CODE = String(TOOL_ID)
const userPower = ref(0)
const router = useRouter()
const projectList = ref([])
const hoveredProjectId = ref(null)
const renameDialogVisible = ref(false)
const currentRenameProject = ref(null)
const newProjectName = ref('')
const newProjectDialogVisible = ref(false)
const newProjectNameInput = ref('')
const projectSearchText = ref('')
const projectSortBy = ref('createTime')

const getUserInfo = () => {
  const userInfo = localStorage.getItem('userInfo')
  if (userInfo) {
    userPower.value = JSON.parse(userInfo).totalAmount || 0
  } else {
    ElMessage.warning('获取用户信息失败')
  }
}

onMounted(() => {
  try {
    document.title = '视频混剪（快速版）'
    const token = localStorage.getItem('token')
    if (!token) {
      ElMessage.warning('请先登录再使用该工具')
      nextTick(() => {
        router.push('/login')
      })
      return
    }
    getUserInfo()
    fetchProjectList()
  } catch (error) {
    console.error('组件初始化失败:', error)
    ElMessage.error('组件初始化失败，请刷新页面重试')
  }
})

watch(projectSortBy, (newSortBy, oldSortBy) => {
  if (newSortBy && newSortBy !== oldSortBy) {
    ElMessage.success(`已按${getSortLabel(newSortBy)}排序`)
  }
})

const filteredProjectList = computed(() => {
  let result = projectList.value

  if (projectSearchText.value.trim()) {
    const keyword = projectSearchText.value.toLowerCase()
    result = result.filter((project) =>
      project.projectName.toLowerCase().includes(keyword)
      || String(project.id).includes(keyword)
      || (project.description || '').toLowerCase().includes(keyword),
    )
  }

  if (projectSortBy.value) {
    result = [...result].sort((a, b) => {
      switch (projectSortBy.value) {
        case 'createTime':
          return new Date(b.createDate) - new Date(a.createDate)
        case 'name':
          return a.projectName.localeCompare(b.projectName, 'zh-CN')
        case 'nameDesc':
          return b.projectName.localeCompare(a.projectName, 'zh-CN')
        case 'createTimeAsc':
          return new Date(a.createDate) - new Date(b.createDate)
        default:
          return 0
      }
    })
  }

  return result
})

let isProjectLoading = false

function handleProjectHover(projectId) {
  hoveredProjectId.value = projectId
}

function handleProjectLeave() {
  hoveredProjectId.value = null
}

function handleProjectClick(project) {
  if (isProjectLoading) return
  isProjectLoading = true
  try {
    const target = router.resolve({
      path: '/detail/D64Edit',
      query: {
        projectId: project.id,
        projectName: project.projectName,
        code: TOOL_CODE,
      },
    })
    const editWindow = window.open(target.href, '_blank')
    if (editWindow) {
      editWindow.opener = null
    }
  } catch (error) {
    console.error('跳转失败:', error)
    ElMessage.error('跳转失败，请重试')
  } finally {
    setTimeout(() => {
      isProjectLoading = false
    }, 100)
  }
}

function handleRenameProject(project) {
  currentRenameProject.value = project
  newProjectName.value = project.projectName
  renameDialogVisible.value = true
}

async function handleCopyProject(project) {
  try {
    const { value: copyProjectName } = await ElMessageBox.prompt(
      '请输入复制后的项目名称',
      '复制项目',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: `${project.projectName} 副本`,
        inputPattern: /\S+/,
        inputErrorMessage: '项目名称不能为空',
      },
    )

    const res = await copyProject(project.id)
    if (res && res.code === 0) {
      const newProjectId = res.id
      if (newProjectId) {
        await renameProject(newProjectId, copyProjectName.trim())
        const itemRes = await getProjectsItemInfo(project.id)
        if (itemRes?.code === 0 && itemRes.itemInfo?.params) {
          await addOrUpdateProjectParams(newProjectId, itemRes.itemInfo.params)
        } else {
          await addOrUpdateProjectParams(newProjectId, JSON.stringify({ systemParams: { code: TOOL_CODE } }))
        }
      }
      ElMessage.success('项目复制成功')
      await fetchProjectList()
    } else {
      ElMessage.error(res.msg || '复制失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('复制项目失败:', error)
      ElMessage.error('复制失败')
    }
  }
}

async function handleDeleteProject(project) {
  try {
    await ElMessageBox.confirm(
      `确定要删除项目"${project.projectName}"吗？`,
      '确认删除',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      },
    )

    const res = await deleteProject(project.id)
    if (res && res.code === 0) {
      ElMessage.success('删除成功')
      await fetchProjectList()
    } else {
      ElMessage.error(res.msg || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除项目失败:', error)
      ElMessage.error('删除失败')
    }
  }
}

async function confirmRename() {
  if (!newProjectName.value.trim()) {
    ElMessage.warning('项目名称不能为空')
    return
  }

  if (!currentRenameProject.value) return

  try {
    const res = await renameProject(currentRenameProject.value.id, newProjectName.value.trim())
    if (res && res.code === 0) {
      ElMessage.success('重命名成功')
      await fetchProjectList()
      renameDialogVisible.value = false
      currentRenameProject.value = null
      newProjectName.value = ''
    } else {
      ElMessage.error(res.msg || '重命名失败')
    }
  } catch (error) {
    console.error('重命名项目失败:', error)
    ElMessage.error('重命名失败')
  }
}

function handleNewProject() {
  newProjectNameInput.value = ''
  newProjectDialogVisible.value = true
}

async function confirmNewProject() {
  if (!newProjectNameInput.value.trim()) {
    ElMessage.warning('项目名称不能为空')
    return
  }

  try {
    const res = await addProject(newProjectNameInput.value.trim(), TOOL_ID)
    if (res && res.code === 0) {
      if (res.id) {
        await addOrUpdateProjectParams(res.id, JSON.stringify({ systemParams: { code: TOOL_CODE } }))
      }
      ElMessage.success('项目创建成功')
      await fetchProjectList()
      newProjectDialogVisible.value = false
      newProjectNameInput.value = ''
    } else {
      ElMessage.error(res.msg || '创建失败')
    }
  } catch (error) {
    console.error('创建项目失败:', error)
    ElMessage.error('创建失败')
  }
}

async function fetchProjectList() {
  try {
    const res = await getProjectList(TOOL_ID)
    if (res && res.code === 0 && res.list) {
      projectList.value = res.list
    } else {
      projectList.value = []
    }
  } catch (error) {
    console.error('获取项目列表失败:', error)
    projectList.value = []
  }
}

function formatDate(dateString) {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch (error) {
    return dateString
  }
}

function clearSort() {
  projectSortBy.value = ''
}

function getSortLabel(sortBy) {
  const sortLabels = {
    createTime: '按创建时间(新→旧)',
    createTimeAsc: '按创建时间(旧→新)',
    name: '按名称(A→Z)',
    nameDesc: '按名称(Z→A)',
  }
  return sortLabels[sortBy] || '未知排序'
}
</script>

<style scoped>
.home-layout {
  height: 100%;
  width: 100%;
}

.home-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
}

.project-list-container {
  width: 100%;
  background-color: #262626;
  border-radius: 4px;
  margin: 12px;
  padding: 16px;
  height: calc(100vh - 80px);
  box-sizing: border-box;
}

.project-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.header-actions {
  display: flex;
  align-items: center;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-title h3 {
  margin: 0;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
}

.project-count {
  color: #999;
  font-size: 12px;
  font-weight: normal;
}

.sort-indicator {
  margin-left: 8px;
}

.project-list {
  display: flex;
  flex-wrap: wrap;
  padding-top: 12px;
  gap: 12px;
  height: calc(100vh - 200px);
  scrollbar-width: thin;
  scrollbar-color: #666 #262626;
  overflow-y: auto;
}

.project-list {
  --project-card-width: 240px;
  --project-card-height: 390px;
  --project-card-padding: 12px;
  --project-thumbnail-size: calc(var(--project-card-width) - var(--project-card-padding) * 2);
}

.project-card {
  background-color: #1e1e1e;
  width: var(--project-card-width);
  height: var(--project-card-height);
  border-radius: 8px;
  padding: 0 var(--project-card-padding) var(--project-card-padding);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #333;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.project-card:hover {
  border-color: #409EFF;
  box-shadow: 0 0 8px rgba(64, 158, 255, 0.2);
  transform: translateY(-2px);
}

.project-card:active {
  transform: translateY(0);
}

.project-thumbnail {
  position: relative;
  width: calc(100% + var(--project-card-padding) * 2);
  height: calc(var(--project-card-width) - 2px);
  margin-left: calc(var(--project-card-padding) * -1);
  margin-right: calc(var(--project-card-padding) * -1);
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #333;
  color: #666;
  font-size: 32px;
}

.project-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-height: 0;
  position: relative;
  padding-bottom: 36px;
}

.project-name {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 32px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-break: break-all;
  margin-bottom: -2px;
}

.project-meta,
.project-date {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.project-meta .el-icon,
.project-date .el-icon {
  flex-shrink: 0;
  font-size: 13px;
}

.project-id {
  color: #888;
  font-size: 11px;
}

.project-date {
  color: #888;
  font-size: 11px;
}

.project-date span,
.project-id span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-copy-preview {
  color: #999;
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 4px;
  animation: fadeIn 0.2s ease forwards;
  z-index: 10;
  min-height: 28px;
  background-color: #1e1e1e;
}

.project-overlay :deep(.el-button) {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 4px 2px;
  font-size: 12px;
}

.project-overlay :deep(.el-button + .el-button) {
  margin-left: 0;
}

.project-overlay :deep(.el-icon) {
  margin-right: 2px;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #1e1e1e;
  color: #666;
  width: var(--project-card-width);
  height: var(--project-card-height);
  border-radius: 8px;
  padding: var(--project-card-padding);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #333;
  box-sizing: border-box;
}

.empty-state p {
  margin: 16px 0;
  font-size: 14px;
}

/* elephant-web 浅色主题 */
.home-layout {
  background-color: var(--bg-app, #f3f4f6);
  background-image:
    radial-gradient(circle at 10% 20%, rgba(147, 51, 234, 0.08) 0%, transparent 40%),
    radial-gradient(circle at 90% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 40%);
}

.home-content {
  padding: 0;
  box-sizing: border-box;
}

.project-list-container {
  height: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: var(--radius-lg, 24px);
  box-shadow: var(--shadow-float);
}

.project-list-header {
  min-height: 52px;
  margin: 0;
  padding: 12px 24px;
  gap: 16px;
  border-bottom: 1px solid var(--border-lighter, #f3f4f6);
  box-sizing: border-box;
}

.header-title h3 {
  color: var(--text-primary, #1f2937);
  font-size: 18px;
  font-weight: 700;
}

.project-count,
.project-id,
.project-date,
.project-copy-preview {
  color: var(--text-secondary, #6b7280);
}

.project-list {
  --project-card-width: auto;
  --project-card-height: 390px;
  flex: 1;
  height: auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  align-content: start;
  gap: 24px;
  padding: 24px;
  scrollbar-color: rgba(107, 114, 128, 0.3) transparent;
}

.project-card {
  width: auto;
  background: #fff;
  border-color: var(--border-light, #e5e7eb);
  border-radius: var(--radius-sm, 12px);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.project-card:hover {
  border-color: var(--accent-blue, #4f46e5);
  box-shadow: var(--shadow-hover, 0 10px 25px rgba(0, 0, 0, 0.05));
}

.project-thumbnail {
  height: 238px;
  border-radius: var(--radius-sm, 12px) var(--radius-sm, 12px) 0 0;
}

.thumbnail-placeholder {
  color: var(--text-muted, #9ca3af);
  background: linear-gradient(135deg, #eef2ff, #f5f3ff);
}

.project-name {
  color: var(--text-primary, #1f2937);
  font-size: 15px;
  font-weight: 700;
}

.project-overlay {
  gap: 6px;
  background: rgba(255, 255, 255, 0.96);
}

.empty-state {
  width: auto;
  background: var(--bg-card, #f9fafb);
  color: var(--text-secondary, #6b7280);
  border: 2px dashed var(--border-light, #e5e7eb);
  border-radius: var(--radius-sm, 12px);
}

.empty-state:hover {
  color: var(--accent-blue, #4f46e5);
  border-color: var(--accent-blue, #4f46e5);
  background: #eef2ff;
  transform: translateY(-2px);
}

/* 与“自动化生产视频”统一的搜索区和项目卡片规范 */
.project-list-container {
  padding: 30px 40px;
  background: var(--bg-panel, #fff);
}

.project-list-header {
  min-height: 58px;
  padding: 14px 16px;
  margin-bottom: 16px;
  gap: 16px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(229, 231, 235, 0.9);
  border-radius: var(--radius-md, 16px);
  backdrop-filter: blur(10px);
}

.header-title h3 {
  font-size: 16px;
}

.project-count {
  padding: 4px 10px;
  font-size: 12px;
  border: 1px solid rgba(79, 70, 229, 0.15);
  border-radius: 999px;
  background: rgba(79, 70, 229, 0.06);
}

.header-actions {
  gap: 8px;
}

.sort-select {
  width: 160px;
}

.project-search {
  width: 320px;
}

.project-list {
  --project-card-width: 240px;
  --project-card-height: 300px;
  --project-card-padding: 12px;
  display: flex;
  flex-wrap: wrap;
  height: auto;
  flex: 1;
  align-content: flex-start;
  gap: 14px;
  padding: 12px 0 16px;
}

.project-card,
.empty-state {
  width: var(--project-card-width);
  height: var(--project-card-height);
  flex: 0 0 var(--project-card-width);
  border-radius: 16px;
}

.project-card {
  background: linear-gradient(
    180deg,
    rgba(79, 70, 229, 0.03) 0%,
    rgba(147, 51, 234, 0.015) 35%,
    rgba(255, 255, 255, 0.85) 100%
  );
  border: 1px solid rgba(229, 231, 235, 0.95);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.project-card:hover {
  transform: translateY(-5px);
  border-color: var(--accent-blue, #4f46e5);
  box-shadow: var(--shadow-hover, 0 10px 25px rgba(0, 0, 0, 0.08)), 0 0 0 1px rgba(79, 70, 229, 0.08);
}

.project-thumbnail {
  height: 160px;
  border-radius: 16px 16px 0 0;
}

.project-name {
  color: var(--text-primary, #1f2937);
  font-size: 15px;
  font-weight: 600;
}

.project-overlay {
  gap: 6px;
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid rgba(79, 70, 229, 0.18);
}

.empty-state {
  color: var(--text-secondary, #6b7280);
  background: var(--bg-card, #f9fafb);
  border: 1px dashed rgba(209, 213, 219, 0.95);
}

.empty-state:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 40px rgba(79, 70, 229, 0.12);
}

@media (max-width: 760px) {
  .project-list-container {
    padding: 20px;
  }

  .project-list-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .project-search {
    width: min(320px, 100%);
  }

  .project-list {
    grid-template-columns: 1fr;
    padding: 16px;
  }
}
</style>
